import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * Who buys: Kitchen, Beverage, … (owner, 2026-11-01: "saket manages kitchen purchase
 * and beverage purchase"). A person is given departments under Staff → their screen →
 * Stations & stock; they then see and enter only those departments' purchases.
 */
export default function Departments({ departments, canManage, onAdd, onEdit }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    if (!name.trim()) return;
    setBusy(true);
    const result = await onAdd({ name: name.trim(), sort_order: departments.length + 1 });
    setBusy(false);
    if (result.ok) {
      toast.success(`${result.data.name} added`);
      setName("");
    } else toast.error(`Not added: ${result.error}`);
  };
  const rename = async dept => {
    const next = window.prompt(`New name for ${dept.name}`, dept.name);
    if (!next || next.trim() === dept.name) return;
    const result = await onEdit(dept.id, { name: next.trim() });
    if (result.ok) toast.success(`Renamed to ${result.data.name}`);
    else toast.error(`Not renamed: ${result.error}`);
  };
  const toggle = async dept => {
    const result = await onEdit(dept.id, { is_active: !dept.is_active });
    if (result.ok) toast.success(`${dept.name} ${result.data.is_active ? "switched on" : "switched off"}`);
    else toast.error(`Not changed: ${result.error}`);
  };

  return (
    <section className="space-y-base max-w-xl" data-testid="purchase-departments">
      <p className="text-meta text-muted-foreground">
        Each purchase is for a department. Give people the departments they buy for under Staff → their screen →
        Stations & stock; someone with none set buys for all of them.
      </p>
      <div className="divide-y rounded-lg border bg-card">
        {departments.map(dept => (
          <div key={dept.id} className="flex items-center gap-snug px-base py-snug" data-testid={`department-${dept.id}`}>
            <span className={`flex-1 text-body font-semibold ${dept.is_active ? "" : "text-muted-foreground line-through"}`}>{dept.name}</span>
            {canManage && (
              <>
                <Button size="sm" variant="ghost" onClick={() => rename(dept)} data-testid={`department-${dept.id}-rename`}>Rename</Button>
                <Button size="sm" variant="outline" onClick={() => toggle(dept)} data-testid={`department-${dept.id}-toggle`}>
                  {dept.is_active ? "Switch off" : "Switch on"}
                </Button>
              </>
            )}
          </div>
        ))}
      </div>
      {canManage && (
        <div className="flex gap-snug">
          <Input value={name} placeholder="e.g. Bar, Housekeeping" maxLength={60} onChange={e => setName(e.target.value)}
                 data-testid="department-new-name" />
          <Button variant="action" disabled={busy || !name.trim()} onClick={add} data-testid="department-add">
            <Plus className="w-4 h-4" />Add
          </Button>
        </div>
      )}
    </section>
  );
}
